<script setup lang="ts">
import type { ToastVariant } from '~kui/types/toast';

import { Icon } from '~ds/components/ui/icon';
import { ICONS } from '~ds/lib/icons';
import { ToastClose, ToastDescription, ToastRoot, ToastTitle } from 'reka-ui';
import { useI18n } from 'vue-i18n';

interface Props {
  variant: ToastVariant;
  message: string;
  title?: string;
  duration?: number;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  duration: 5000,
});

const emit = defineEmits<{
  close: [];
}>();

const variantIconMap = {
  success: ICONS.checkmarkCircle,
  error: ICONS.alertCircle,
  warning: ICONS.alert,
} as const;

const toastVariants = cva('flex items-center gap-3 rounded-full px-4 py-3 text-foreground', {
  variants: {
    variant: {
      success: 'bg-secondary',
      error: 'bg-brand-coral',
      warning: 'bg-brand-orange',
    },
  },
});

const variantClasses = computed<string>(() =>
  twMerge(toastVariants({ variant: props.variant })),
);

const icon = computed(() => variantIconMap[props.variant]);

const { t } = useI18n();

const resolvedTitle = computed(() =>
  props.title ?? t(`unified.toast.defaultTitle.${props.variant}`),
);
</script>

<template>
  <ToastRoot
    data-testid="kui-toast"
    :duration="duration"
    :class="variantClasses"
    class="pointer-events-auto data-[state=open]:animate-slideIn data-[state=closed]:animate-fadeOut data-[swipe=move]:translate-x-(--reka-toast-swipe-move-x) data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-transform"
    @update:open="(open: boolean) => { if (!open) emit('close') }"
  >
    <Icon
      :name="icon"
      size="lg"
      class="shrink-0"
    />

    <div class="flex flex-1 flex-col gap-px">
      <ToastTitle class="text-sm font-semibold">
        {{ resolvedTitle }}
      </ToastTitle>
      <ToastDescription class="whitespace-pre-line text-xs text-foreground">
        {{ message }}
      </ToastDescription>
    </div>

    <ToastClose
      :aria-label="$t('unified.toast.closeNotification')"
      class="shrink-0 cursor-pointer rounded-full bg-white/60 p-1 text-foreground transition-colors hover:bg-white"
      @click="emit('close')"
    >
      <Icon
        :name="ICONS.cancel"
        size="sm"
      />
    </ToastClose>
  </ToastRoot>
</template>
